import { useRef, useState } from "react";
import { ApiError } from "../api/client";
import { useSession } from "../session";
import { ErrorNote } from "../ui/States";
import { useLoad } from "../ui/useLoad";
import { editable, finalizeForm, type UploadQueue, type UploadRow, type VideoFormValue } from "./uploadQueue";

export type CommonInput = { tags: string; gids: string; series: string; start: string };
export function commonForms(
  rows: UploadRow[],
  selected: string[],
  input: CommonInput,
  occupied: number[] = [],
): Map<string, VideoFormValue> {
  const targets = rows.filter((r) => selected.includes(r.id) && editable(r));
  if (!targets.length) throw new Error("전송 전 영상을 선택하세요.");
  const adding = input.tags
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const addGids = input.gids.split(/[,\s]+/).filter(Boolean);
  if (addGids.some((p) => !/^[1-9]\d{0,14}$/.test(p))) throw new Error("공통 GID를 올바르게 입력하세요.");
  const numbered = !["keep", "none"].includes(input.series);
  if (numbered && (!/^\d{1,3}$/.test(input.start) || Number(input.start) + targets.length - 1 > 999))
    throw new Error("배정할 회차가 0~999 범위를 벗어납니다.");
  const forms = new Map<string, VideoFormValue>();
  targets.forEach((r, index) => {
    const normalized = finalizeForm(r.form);
    const tags = [...new Set([...(normalized.tags ?? []), ...adding])];
    const gids = [...new Set([...(normalized.gids ?? []), ...addGids.map(Number)])];
    const form: VideoFormValue = {
      ...r.form,
      tags,
      tagText: "",
      gids,
      gidText: gids.join(", "),
      ...(input.series === "none"
        ? { seriesId: null, episodeNo: null, episodeText: undefined }
        : numbered
          ? {
              seriesId: input.series,
              episodeNo: Number(input.start) + index,
              episodeText: String(Number(input.start) + index),
            }
          : {}),
    };
    finalizeForm(form);
    if (
      numbered &&
      (occupied.includes(form.episodeNo!) ||
        rows.some(
          (other) =>
            !targets.includes(other) &&
            !["failed", "cancelled"].includes(other.phase) &&
            other.state?.status !== "removed" &&
            other.form.seriesId === input.series &&
            Number(other.form.episodeText ?? other.form.episodeNo) === form.episodeNo,
        ))
    )
      throw new Error(`${form.episodeNo}회차가 기존 작품 또는 대기열과 겹칩니다.`);
    forms.set(r.id, form);
  });
  return forms;
}

export function UploadCommon({
  queue,
  rows,
  selected,
  onDone,
}: {
  queue: UploadQueue;
  rows: UploadRow[];
  selected: string[];
  onDone: () => void;
}) {
  const { client } = useSession();
  const [input, setInput] = useState<CommonInput>({ tags: "", gids: "", series: "keep", start: "1" });
  const [newSeries, setNewSeries] = useState("");
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState<unknown>();
  const selectionRef = useRef(selected);
  selectionRef.current = selected;
  const series = useLoad(
    () => client.call("videos.seriesList", { query: { mine: true, limit: 100 } }),
    `upload-common-series:${rows.map((r) => r.form.seriesId ?? "").join(",")}`,
  );
  const numbered = !["keep", "none"].includes(input.series);
  const existing = useLoad(
    () =>
      numbered ? client.call("videos.seriesGet", { params: { id: input.series } }) : Promise.resolve(null),
    input.series,
  );
  const targets = rows.filter((r) => selected.includes(r.id) && editable(r));
  let previewError = "";
  try {
    commonForms(
      rows,
      selected,
      input,
      existing.data?.episodes
        .filter((v) => !["failed", "removed"].includes(v.state?.status ?? ""))
        .map((v) => v.series?.episodeNo)
        .filter((n): n is number => n !== null && n !== undefined),
    );
  } catch (e) {
    previewError = e instanceof Error ? e.message : "공통 설정을 확인하세요.";
  }
  const set = (patch: Partial<CommonInput>) => {
    setInput({ ...input, ...patch });
    setError(undefined);
  };
  return (
    <section className="upload-common" aria-label="선택 영상 공통 설정">
      <div className="upload-section-head">
        <h2>선택 {targets.length}개 공통 설정</h2>
        <button type="button" className="btn" onClick={onDone}>
          닫기
        </button>
      </div>
      <p>태그와 원작은 기존 값에 추가합니다. 제목과 설명은 그대로 유지합니다.</p>
      <fieldset disabled={busy}>
        <label>
          추가할 공통 태그 (쉼표로 구분)
          <input value={input.tags} onChange={(e) => set({ tags: e.target.value })} />
        </label>
        <label>
          추가할 공통 원작 GID
          <input value={input.gids} onChange={(e) => set({ gids: e.target.value })} />
        </label>
        <label>
          공통 작품
          <select value={input.series} onChange={(e) => set({ series: e.target.value })}>
            <option value="keep">기존 작품 유지</option>
            <option value="none">작품 없음</option>
            {series.data?.items.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>
        </label>
        <div className="video-actions">
          <label>
            새 공통 작품 이름
            <input maxLength={120} value={newSeries} onChange={(e) => setNewSeries(e.target.value)} />
          </label>
          <button
            type="button"
            className="btn"
            disabled={!newSeries.trim()}
            onClick={async () => {
              if (busyRef.current) return;
              busyRef.current = true;
              setBusy(true);
              try {
                const created = await client.call("videos.seriesCreate", {
                  body: { title: newSeries.trim() },
                });
                series.reload();
                setInput((v) => ({ ...v, series: created.id }));
                setNewSeries("");
              } catch (e) {
                setError(e);
              } finally {
                busyRef.current = false;
                setBusy(false);
              }
            }}
          >
            새 작품 만들기
          </button>
        </div>
        <small>이 버튼은 작품을 서버에 저장합니다. 파일에는 아래 적용 버튼으로 지정합니다.</small>
        {numbered && (
          <>
            <label>
              시작 회차 (0은 예고편)
              <input
                type="number"
                min={0}
                max={999}
                step={1}
                value={input.start}
                onChange={(e) => set({ start: e.target.value })}
              />
            </label>
            <ol className="upload-episode-preview" aria-label="회차 배정 미리보기">
              {targets.map((r, i) => (
                <li key={r.id}>
                  <span>{r.name}</span>
                  <strong>{/^\d{1,3}$/.test(input.start) ? Number(input.start) + i : "?"}회</strong>
                </li>
              ))}
            </ol>
            {existing.status === "loading" && <p role="status">기존 작품 회차를 확인하고 있습니다.</p>}
            {existing.status === "error" && (
              <>
                <p role="alert">기존 회차를 확인하지 못했습니다. 적용 전에 다시 확인하세요.</p>
                <button type="button" className="btn" onClick={existing.reload}>
                  기존 회차 다시 확인
                </button>
              </>
            )}
            {existing.status === "ready" && !previewError && (
              <p>현재 조회 결과와 겹치는 회차가 없습니다. 등록 시 서버가 다시 확인합니다.</p>
            )}
          </>
        )}
      </fieldset>
      {series.status === "error" && <ErrorNote error={series.error} onRetry={series.reload} />}
      {previewError && <p role="alert">{previewError}</p>}
      {error !== undefined && (
        <ErrorNote
          error={error}
          message={error instanceof Error && !(error instanceof ApiError) ? error.message : undefined}
        />
      )}
      <button
        type="button"
        className="btn btn-pri"
        disabled={busy || !!previewError || (numbered && existing.status !== "ready")}
        onClick={async () => {
          if (busyRef.current) return;
          busyRef.current = true;
          setBusy(true);
          setError(undefined);
          const before = queue.getSnapshot().rows;
          try {
            const detail = numbered
              ? await client.call("videos.seriesGet", { params: { id: input.series } })
              : null;
            const forms = commonForms(
              before,
              selected,
              input,
              detail?.episodes
                .filter((v) => !["failed", "removed"].includes(v.state?.status ?? ""))
                .map((v) => v.series?.episodeNo)
                .filter((n): n is number => n !== null && n !== undefined),
            );
            const originals = new Set<number>();
            for (const form of forms.values()) {
              const finalized = finalizeForm(form);
              const result = await client.call("videos.validateTags", {
                body: { tags: finalized.tags ?? [] },
              });
              if (result.blockedTags.length)
                throw new Error(`차단된 태그는 적용할 수 없습니다: ${result.blockedTags.join(", ")}`);
              for (const gid of finalized.gids ?? []) originals.add(gid);
            }
            for (const gid of originals) {
              const gallery = await client.call("catalog.get", { params: { gid } }).catch(() => {
                throw new Error(`원작 GID ${gid}를 확인하지 못했습니다. 내용을 확인한 뒤 다시 적용하세요.`);
              });
              const tags = [...new Set(gallery.tags)];
              for (let offset = 0; offset < tags.length; offset += 30) {
                const validation = await client.call("videos.validateTags", {
                  body: { tags: tags.slice(offset, offset + 30) },
                });
                if (validation.blockedTags.length)
                  throw new Error(`차단된 원작 GID ${gid}는 적용할 수 없습니다.`);
              }
            }
            const current = queue.getSnapshot().rows;
            if (
              selected.join() !== selectionRef.current.join() ||
              before.map((r) => r.id).join() !== current.map((r) => r.id).join() ||
              [...forms.keys()].some(
                (id) =>
                  !current.some(
                    (r) => r.id === id && editable(r) && r.form === before.find((v) => v.id === id)?.form,
                  ),
              )
            )
              throw new Error("선택 영상이 변경되었습니다. 내용을 확인한 뒤 다시 적용하세요.");
            queue.apply(forms);
            onDone();
          } catch (e) {
            setError(e);
          } finally {
            busyRef.current = false;
            setBusy(false);
          }
        }}
      >
        {busy ? "확인 중" : `선택 ${targets.length}개에 적용`}
      </button>
    </section>
  );
}
